// src/modules/app-views/invoices/pages/Init/modals/PrinterConfigModal/Step1.jsx
import { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  Button,
  Switch,
  Space,
  Typography,
  Alert,
  Card,
  Tag,
  Tooltip,
  Flex,
  Segmented,
  Input,
} from 'antd';
import {
  PrinterOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  WindowsOutlined,
  CodeOutlined,
  WifiOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import invoicesService from 'modules/app-views/invoices/service';
import { getStorageModuleItem, setStorageModuleItem } from 'stores/module';

const { Text } = Typography;

// Known thermal printer USB vendor IDs (for filtering serial ports)
const KNOWN_PRINTER_VENDORS = [
  { usbVendorId: 0x04b8 }, // Epson
  { usbVendorId: 0x0b1b }, // Bematech
  { usbVendorId: 0x0dd4 }, // Elgin
  { usbVendorId: 0x0519 }, // Star Micronics
  { usbVendorId: 0x0416 }, // Winbond
  { usbVendorId: 0x067b }, // Prolific (USB-Serial adapters)
  { usbVendorId: 0x0403 }, // FTDI (USB-Serial adapters)
];

// Known printer models by vendorId + productId
const KNOWN_PRINTERS = {
  // Epson
  '1208_514': 'Epson TM-T20X',
  '1208_3587': 'Epson TM-T20',
  '1208_3605': 'Epson TM-T20II',
  '1208_3624': 'Epson TM-T20X',
  '1208_3588': 'Epson TM-T88V',
  // Bematech
  '2843_1': 'Bematech MP-4200 TH',
  '2843_2': 'Bematech MP-4200 TH',
  '2843_3': 'Bematech MP-4200 TH',
  // Elgin
  '3540_512': 'Elgin i9',
};

// Vendor names fallback
const VENDOR_NAMES = {
  1208: 'Epson',
  2843: 'Bematech',
  3540: 'Elgin',
  1305: 'Star Micronics',
};

// Get printer display name from USB info
function getPrinterDisplayName(vendorId, productId) {
  if (!vendorId) return null;

  // Try exact match first
  const key = `${vendorId}_${productId || 0}`;
  if (KNOWN_PRINTERS[key]) {
    return KNOWN_PRINTERS[key];
  }

  // Fallback to vendor name
  const vendor = VENDOR_NAMES[vendorId];
  if (vendor) {
    return `${vendor} (USB)`;
  }

  return 'Impressora Térmica (USB)';
}

// *******************************************************************************
// PRINTER TAB CONTENT COMPONENT
// *******************************************************************************
const PrinterTabContent = ({ type, config, setConfig }) => {
  const [selecting, setSelecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingNetwork, setTestingNetwork] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSelectPrinter = useCallback(async () => {
    if (!navigator.serial) {
      appService.message({ flag: 'error', text: 'web_serial_not_supported' });
      return;
    }

    setSelecting(true);

    try {
      // Request port with filters for known printers
      const port = await navigator.serial.requestPort({
        filters: KNOWN_PRINTER_VENDORS,
      });

      // Get port info
      const info = port.getInfo?.() || {};

      // Build display name using the helper function
      const displayName = getPrinterDisplayName(info.usbVendorId, info.usbProductId);

      // Save printer info
      const printerInfo = {
        configured: true,
        displayName,
        vendorId: info.usbVendorId,
        productId: info.usbProductId,
        configuredAt: new Date().toISOString(),
      };

      setConfig(prev => ({ ...prev, printer: printerInfo }));
      setStorageModuleItem(`${type}Printer`, printerInfo);

      appService.message({ flag: 'success', text: 'printer_configured_success', duration: 2 });
    } catch (error) {
      if (error.name !== 'NotFoundError') {
        console.error('[PrinterConfig] Error selecting printer:', error);
        appService.message({ flag: 'error', text: 'printer_config_error' });
      }
    } finally {
      setSelecting(false);
    }
  }, [type, setConfig]);

  const handleTestPrint = useCallback(async () => {
    if (!config.printer?.configured && config.printMethod === 'escpos') {
      appService.message({ flag: 'warning', text: 'select_printer_first' });
      return;
    }

    setTesting(true);

    try {
      // Create test invoice data
      const testData = {
        issuer: {
          name: 'EMPRESA TESTE',
          fullName: 'EMPRESA TESTE LTDA',
          cnpj: '00.000.000/0001-00',
          ie: 'ISENTO',
          address: {
            address1: 'RUA TESTE',
            number: '123',
            neighborhood: 'CENTRO',
            city: 'SAO PAULO',
            region: 'SP',
            zip: '00000-000',
          },
        },
        items: [
          { description: 'PRODUTO TESTE 1', quantity: 1, unitPrice: 10.0, totalPrice: 10.0 },
          { description: 'PRODUTO TESTE 2', quantity: 2, unitPrice: 5.0, totalPrice: 10.0 },
        ],
        totals: { subtotal: 20.0, discount: 0, total: 20.0 },
        payments: [{ type: 'money', value: 20.0 }],
        consumer: { name: 'CONSUMIDOR', cpf: '' },
        documentNumber: '000001',
        documentSerie: '1',
        issueDate: new Date().toISOString(),
        accessKey: '00000000000000000000000000000000000000000000',
        qrCodeUrl: 'https://exemplo.com/teste',
        protocol: 'TESTE',
        status: 'test',
      };

      if (config.printMethod === 'escpos') {
        // ESC/POS direct printing
        await invoicesService.printDanfe('', {
          forceDirect: true,
          invoiceData: testData,
        });
      } else {
        // Browser printing - opens system print dialog
        await invoicesService.printTestPage();
      }

      // appService.message({ flag: 'success', text: 'test_print_sent', duration: 3 });
    } catch (error) {
      console.error('[PrinterConfig] Test print error:', error);
      appService.message({ flag: 'error', text: 'test_print_failed', duration: 4 });
    } finally {
      setTesting(false);
    }
  }, [config.printer, config.printMethod]);

  const handleAutoPrintChange = useCallback(
    checked => {
      setConfig(prev => ({ ...prev, autoPrint: checked }));
      setStorageModuleItem(`${type}AutoPrint`, checked);
    },
    [type, setConfig]
  );

  const handlePrintMethodChange = useCallback(
    value => {
      setConfig(prev => ({ ...prev, printMethod: value }));
      setStorageModuleItem(`${type}PrintMethod`, value);
    },
    [type, setConfig]
  );

  const handleClearPrinter = useCallback(() => {
    setConfig(prev => ({ ...prev, printer: null }));
    setStorageModuleItem(`${type}Printer`, null);
    appService.message({ flag: 'info', text: 'printer_config_cleared', duration: 2 });
  }, [type, setConfig]);

  // Network printer handlers
  const handleNetworkIpChange = useCallback(
    e => {
      const ip = e.target.value;
      setConfig(prev => ({ ...prev, networkPrinterIp: ip }));
      setStorageModuleItem(`${type}NetworkPrinterIp`, ip);
    },
    [type, setConfig]
  );

  const handleNetworkPortChange = useCallback(
    e => {
      const port = e.target.value;
      setConfig(prev => ({ ...prev, networkPrinterPort: port }));
      setStorageModuleItem(`${type}NetworkPrinterPort`, port);
    },
    [type, setConfig]
  );

  const handleTestNetworkPrinter = useCallback(async () => {
    if (!config.networkPrinterIp) {
      appService.message({ flag: 'warning', text: 'enter_printer_ip' });
      return;
    }

    setTestingNetwork(true);

    try {
      const result = await invoicesService.testNetworkPrinter({
        printerIp: config.networkPrinterIp,
        printerPort: config.networkPrinterPort || 9100,
        printTestPage: true,
      });

      if (result.success) {
        appService.message({ flag: 'success', text: 'network_printer_connected', duration: 3 });
      } else {
        appService.message({
          flag: 'error',
          text: result.error || 'network_printer_connection_failed',
          duration: 4,
        });
      }
    } catch (error) {
      console.error('[PrinterConfig] Network test error:', error);
      appService.message({ flag: 'error', text: 'network_printer_connection_failed', duration: 4 });
    } finally {
      setTestingNetwork(false);
    }
  }, [config.networkPrinterIp, config.networkPrinterPort]);

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  const typeLabel = type === 'nfce' ? 'NFC-e' : 'NF-e';
  const hasPrinter = config.printer?.configured;
  const isNetworkMethod = config.printMethod === 'network';
  const isBrowserMethod = config.printMethod === 'browser' || !config.printMethod;
  const isEscPosMethod = config.printMethod === 'escpos';

  return (
    <div style={{ padding: '8px 0' }}>
      {/* Print Method Selection */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Text strong>{translateX('print_method')}</Text>
          <Segmented
            block
            value={config.printMethod || 'browser'}
            onChange={handlePrintMethodChange}
            options={[
              {
                label: (
                  <Space size={4}>
                    <WindowsOutlined />
                    <span>{translateX('os')}</span>
                  </Space>
                ),
                value: 'browser',
              },
              {
                label: (
                  <Space size={4}>
                    <WifiOutlined />
                    <span>{translateX('network')}</span>
                  </Space>
                ),
                value: 'network',
              },
              {
                label: (
                  <Space size={4}>
                    <CodeOutlined />
                    <span>ESC/POS</span>
                  </Space>
                ),
                value: 'escpos',
              },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {config.printMethod === 'escpos'
              ? translateX('escpos_method_description')
              : config.printMethod === 'network'
                ? translateX('network_method_description')
                : translateX('browser_method_description')}
          </Text>
        </Space>
      </Card>

      {/* Printer Selection - Only show for ESC/POS method */}
      {isEscPosMethod && (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text strong>{translateX('thermal_printer')}</Text>

            {hasPrinter ? (
              // Configured printer - single row with name, tag, and action buttons
              <Flex
                align="center"
                justify="space-between"
                style={{
                  padding: '8px 12px',
                  background: 'var(--ant-color-bg-container-disabled)',
                  borderRadius: 6,
                }}
              >
                <Space>
                  <Text>{config.printer.displayName}</Text>
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    {translateX('connected')}
                  </Tag>
                </Space>
                <Space size="small">
                  <Tooltip title={translateX('test_printer')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={handleTestPrint}
                      loading={testing}
                    />
                  </Tooltip>
                  <Tooltip title={translateX('remove_printer')}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleClearPrinter}
                    />
                  </Tooltip>
                </Space>
              </Flex>
            ) : (
              <Alert
                type="info"
                showIcon
                message={translateX('escpos_requires_usb_printer')}
                style={{ marginBottom: 8 }}
              />
            )}

            {/* Select printer button - only for ESC/POS when no printer configured */}
            {!hasPrinter && (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handleSelectPrinter}
                loading={selecting}
                block
              >
                {translateX('select_printer')}
              </Button>
            )}
          </Space>
        </Card>
      )}

      {/* Browser method info */}
      {isBrowserMethod && (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text strong>{translateX('thermal_printer')}</Text>
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={translateX('browser_uses_system_printer')}
              description={translateX('browser_method_info')}
            />
          </Space>
        </Card>
      )}

      {/* Network Printer Configuration - only show when network method selected */}
      {isNetworkMethod && (
        <Card size="small" style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text strong>
              <ApiOutlined style={{ marginRight: 4 }} />
              {translateX('network_printer_config')}
            </Text>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder={translateX('printer_ip_placeholder')}
                value={config.networkPrinterIp || ''}
                onChange={handleNetworkIpChange}
                style={{ flex: 1 }}
              />
              <Input
                placeholder="9100"
                value={config.networkPrinterPort || ''}
                onChange={handleNetworkPortChange}
                style={{ width: 80 }}
              />
            </Space.Compact>
            <Button
              icon={<PrinterOutlined />}
              onClick={handleTestNetworkPrinter}
              loading={testingNetwork}
              block
            >
              {translateX('test_network_connection')}
            </Button>
          </Space>
        </Card>
      )}

      {/* Auto Print Switch */}
      <Card size="small" style={{ marginBottom: 0 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Text strong>{translateX('auto_print_after_emission')}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translateX('auto_print_description', { type: typeLabel })}
            </Text>
          </div>
          <Switch checked={config.autoPrint || false} onChange={handleAutoPrintChange} />
        </Space>
      </Card>
    </div>
  );
};

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const PrinterConfigStepContent = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [nfceConfig, setNfceConfig] = useState({
    printer: null,
    autoPrint: false,
    printMethod: 'browser', // 'browser' = system driver, 'network' = via IP, 'escpos' = direct ESC/POS
    networkPrinterIp: '',
    networkPrinterPort: '',
  });

  const [nfeConfig, setNfeConfig] = useState({
    printer: null,
    autoPrint: false,
    printMethod: 'browser',
    networkPrinterIp: '',
    networkPrinterPort: '',
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Load saved configurations on mount
  useEffect(() => {
    const savedNfcePrinter = getStorageModuleItem('nfcePrinter');
    const savedNfceAutoPrint = getStorageModuleItem('nfceAutoPrint');
    const savedNfcePrintMethod = getStorageModuleItem('nfcePrintMethod');
    const savedNfceNetworkIp = getStorageModuleItem('nfceNetworkPrinterIp');
    const savedNfceNetworkPort = getStorageModuleItem('nfceNetworkPrinterPort');
    const savedNfePrinter = getStorageModuleItem('nfePrinter');
    const savedNfeAutoPrint = getStorageModuleItem('nfeAutoPrint');
    const savedNfePrintMethod = getStorageModuleItem('nfePrintMethod');
    const savedNfeNetworkIp = getStorageModuleItem('nfeNetworkPrinterIp');
    const savedNfeNetworkPort = getStorageModuleItem('nfeNetworkPrinterPort');

    setNfceConfig({
      printer: savedNfcePrinter || null,
      autoPrint: savedNfceAutoPrint || false,
      printMethod: savedNfcePrintMethod || 'browser',
      networkPrinterIp: savedNfceNetworkIp || '',
      networkPrinterPort: savedNfceNetworkPort || '',
    });

    setNfeConfig({
      printer: savedNfePrinter || null,
      autoPrint: savedNfeAutoPrint || false,
      printMethod: savedNfePrintMethod || 'browser',
      networkPrinterIp: savedNfeNetworkIp || '',
      networkPrinterPort: savedNfeNetworkPort || '',
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // TAB ITEMS
  // ─────────────────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'nfce',
      label: 'NFC-e',
      children: <PrinterTabContent type="nfce" config={nfceConfig} setConfig={setNfceConfig} />,
    },
    {
      key: 'nfe',
      label: 'NF-e',
      children: <PrinterTabContent type="nfe" config={nfeConfig} setConfig={setNfeConfig} />,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <Alert
        type="info"
        showIcon
        title={translateX('printer_config_info_title')}
        description={translateX('printer_config_info_description')}
        style={{ marginBottom: 8 }}
      />

      <Tabs items={tabItems} defaultActiveKey="nfce" />
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const printerConfigStep = () => ({
  stepTitle: 'printer_settings',
  stepTooltip: 'configure_printers_for_invoices',
  content: () => <PrinterConfigStepContent />,
  validationFields: [],
  nextButtonText: 'close',
  nextButtonIcon: null,
  nextButtonProps: { type: 'default' },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default printerConfigStep;
